import { useCallback } from "react";
import { useCreateTask } from "@api/task.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { toTaskCreateBody } from "../taskPlaceholderFields";
import type { NestedTaskRow } from "../types";
import { hasTaskFields } from "./cells/taskRowCells";

/** Escape, or leaving a draft blank: drops the draft row and its focus. */
const cancelDraft = (row: NestedTaskRow) => {
    row.removePlaceholder?.();
    row.onSetFocusedGroup?.(null);
};

/**
 * Creating tasks from draft rows: `create` saves a question with the draft's
 * group fields and keeps its group focused for the next draft. A failed save
 * shows the board's error toast.
 */
export const useTaskListCreate = () => {
    const toaster = useToaster();
    const { mutate: createTask } = useCreateTask({
        onError: () => toaster.error("Failed to create task"),
    });

    const create = useCallback(
        ({ row, value }: { row: NestedTaskRow; value: string }) => {
            if (!hasTaskFields(row)) {
                return;
            }
            createTask(toTaskCreateBody({ title: value, fields: row }));
            if (row.groupKey) {
                row.onSetFocusedGroup?.(row.groupKey);
            }
        },
        [createTask],
    );

    return { create, cancelDraft };
};
